import { useState, useEffect, useMemo, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, ArrowLeft, AlertTriangle, Compass } from "lucide-react";
import { cn } from "@/lib/utils";
import { getTokenOverlap } from "@/lib/utils/textSimilarity";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { DECISION_TRACKS, DECISION_SUBTRACKS } from "@/lib/decisionTracks";

interface TriggerStepProps {
  initialOptionA: string;
  initialOptionB: string;
  initialPreference: 'a' | 'b' | 'undecided';
  onNext: (optionA: string, optionB: string, preference: 'a' | 'b' | 'undecided') => void;
  onBack: () => void;
}

export const TriggerStep = ({
  initialOptionA,
  initialOptionB,
  initialPreference,
  onNext,
  onBack,
}: TriggerStepProps) => {
  const [optionA, setOptionA] = useState(initialOptionA);
  const [optionB, setOptionB] = useState(initialOptionB);
  const [preference, setPreference] = useState<'a' | 'b' | 'undecided'>(initialPreference);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const optionBRef = useRef<HTMLInputElement>(null);

  const { decisionType, decisionSubtype } = useOnboardingStore();

  // Derive classification label
  const classificationLabel = useMemo(() => {
    const track = DECISION_TRACKS.find(t => t.id === decisionType);
    if (!track) return null;
    const subtrack = decisionType && decisionSubtype
      ? DECISION_SUBTRACKS[decisionType]?.find(s => s.id === decisionSubtype)
      : null;
    return { track: track.label, subtrack: subtrack?.label };
  }, [decisionType, decisionSubtype]);

  const isValid = optionA.trim().length >= 5 && optionB.trim().length >= 5;

  // Similarity guard
  const similarity = useMemo(() => {
    if (optionA.trim().length < 5 || optionB.trim().length < 5) return 0;
    return getTokenOverlap(optionA, optionB);
  }, [optionA, optionB]);

  const showSimilarityWarning = similarity > 0.7;

  // Auto-jump to Option B when Option A has content and user presses Enter/Tab
  const handleOptionAKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && optionA.trim().length >= 5) {
      e.preventDefault();
      optionBRef.current?.focus();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && isValid) {
        onNext(optionA, optionB, preference);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [optionA, optionB, preference, isValid, onNext]);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header – Classification subtle, question dominant */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider">
            Schritt 1 von 4
          </span>
          {classificationLabel && (
            <Badge variant="outline" className="text-xs gap-1 border-primary/30 text-primary">
              <Compass className="h-3 w-3" />
              {classificationLabel.track}
              {classificationLabel.subtrack && ` › ${classificationLabel.subtrack}`}
            </Badge>
          )}
        </div>

        {/* Dominant question – larger, with emphasis on second line */}
        <h2 className="text-[28px] md:text-[34px] font-semibold font-serif leading-[1.2] tracking-tight text-foreground max-w-[720px]">
          Wofür entscheidest du dich –{" "}
          <span className="text-primary">wenn du dich festlegen müsstest?</span>
        </h2>
      </div>

      {/* Option Cards – clear strategic framing */}
      <div className="grid gap-5 md:grid-cols-2 -mx-1 px-1">
        {/* Strategie A Card */}
        <div
          className={cn(
            "relative rounded-xl border p-6 transition-all duration-200",
            focusedField === "a"
              ? "border-primary bg-primary/[0.03] shadow-[0_8px_20px_hsl(var(--primary)/0.06)] scale-[1.005]"
              : focusedField === "b"
                ? "border-border/40 bg-card/40 opacity-70"
                : "border-border bg-card hover:border-primary/40 hover:shadow-sm"
          )}
        >
          <Label
            htmlFor="option-a"
            className="text-sm font-semibold text-foreground flex items-center gap-2.5 mb-1.5"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary text-xs font-bold shrink-0">
              A
            </span>
            Strategie A
          </Label>
          <p className="text-xs text-muted-foreground mb-4 ml-[38px]">
            Beschreibe die Richtung – nicht die Begründung.
          </p>
          <Input
            id="option-a"
            value={optionA}
            onChange={(e) => setOptionA(e.target.value)}
            onFocus={() => setFocusedField("a")}
            onBlur={() => setFocusedField(null)}
            onKeyDown={handleOptionAKeyDown}
            placeholder="z. B. Enterprise On-Premise Fokus"
            className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 h-12 text-base px-0 ml-[38px] w-[calc(100%-38px)]"
            autoFocus
          />
        </div>

        {/* Strategie B Card */}
        <div
          className={cn(
            "relative rounded-xl border p-6 transition-all duration-200",
            focusedField === "b"
              ? "border-primary bg-primary/[0.03] shadow-[0_8px_20px_hsl(var(--primary)/0.06)] scale-[1.005]"
              : focusedField === "a"
                ? "border-border/40 bg-card/40 opacity-70"
                : "border-border bg-card hover:border-primary/40 hover:shadow-sm"
          )}
        >
          <Label
            htmlFor="option-b"
            className="text-sm font-semibold text-foreground flex items-center gap-2.5 mb-1.5"
          >
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary text-xs font-bold shrink-0">
              B
            </span>
            Strategie B
          </Label>
          <p className="text-xs text-muted-foreground mb-4 ml-[38px]">
            Beschreibe die Richtung – nicht die Begründung.
          </p>
          <Input
            ref={optionBRef}
            id="option-b"
            value={optionB}
            onChange={(e) => setOptionB(e.target.value)}
            onFocus={() => setFocusedField("b")}
            onBlur={() => setFocusedField(null)}
            placeholder="z. B. Cloud-First für Mittelstand"
            className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 h-12 text-base px-0 ml-[38px] w-[calc(100%-38px)]"
          />
        </div>
      </div>

      {/* Micro-Instruction */}
      <p className="text-xs text-muted-foreground/70 text-center">
        Definiere zwei mögliche strategische Richtungen. Noch keine Begründung – nur die Optionen.
      </p>

      {/* Similarity Warning */}
      {showSimilarityWarning && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 animate-fade-in">
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">
            Die beiden Optionen wirken sehr ähnlich. Schärfe den Unterschied.
          </p>
        </div>
      )}

      {/* Initial Preference — Bias Tracking */}
      {isValid && (
        <div className="space-y-3 animate-fade-in">
          <Label className="text-sm font-semibold text-foreground">
            Wenn du jetzt entscheiden müsstest – welche Richtung?
          </Label>
          <p className="text-xs text-muted-foreground -mt-1">
            Deine aktuelle Tendenz wird später im Report sichtbar gemacht. Sie beeinflusst die Analyse nicht.
          </p>
          <div className="grid grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => setPreference('a')}
              className={cn(
                "relative rounded-xl border p-4 text-left transition-all duration-200",
                "hover:shadow-sm hover:-translate-y-0.5",
                preference === 'a'
                  ? "border-primary bg-primary/5 shadow-[0_0_15px_hsl(var(--primary)/0.1)]"
                  : "border-border bg-card hover:border-primary/40"
              )}
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary text-xs font-bold mb-2">
                A
              </span>
              <p className="text-sm font-medium text-foreground truncate">
                {optionA.trim().substring(0, 30) || "Strategie A"}
              </p>
            </button>
            <button
              type="button"
              onClick={() => setPreference('b')}
              className={cn(
                "relative rounded-xl border p-4 text-left transition-all duration-200",
                "hover:shadow-sm hover:-translate-y-0.5",
                preference === 'b'
                  ? "border-primary bg-primary/5 shadow-[0_0_15px_hsl(var(--primary)/0.1)]"
                  : "border-border bg-card hover:border-primary/40"
              )}
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary text-xs font-bold mb-2">
                B
              </span>
              <p className="text-sm font-medium text-foreground truncate">
                {optionB.trim().substring(0, 30) || "Strategie B"}
              </p>
            </button>
            <button
              type="button"
              onClick={() => setPreference('undecided')}
              className={cn(
                "relative rounded-xl border p-4 text-left transition-all duration-200",
                "hover:shadow-sm hover:-translate-y-0.5",
                preference === 'undecided'
                  ? "border-primary bg-primary/5 shadow-[0_0_15px_hsl(var(--primary)/0.1)]"
                  : "border-border bg-card hover:border-primary/40"
              )}
            >
              <span className="flex h-6 w-6 items-center justify-center rounded-md bg-muted text-muted-foreground text-xs font-bold mb-2">
                ?
              </span>
              <p className="text-sm font-medium text-foreground">
                Kann ich nicht sagen
              </p>
            </button>
          </div>
        </div>
      )}

      {/* Footer hint */}
      <div className="flex items-center justify-between text-xs text-muted-foreground/60">
        <span>
          {isValid
            ? "✓ Bereit zum Fortfahren"
            : "Beide Optionen ausfüllen"}
        </span>
        <span>⌘ + Enter</span>
      </div>

      {/* Action buttons */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onBack}
          className="gap-2 h-12 px-6 rounded-xl border-border hover:bg-card hover:border-primary/40"
        >
          <ArrowLeft className="h-4 w-4" />
          Zurück
        </Button>
        <Button
          onClick={() => onNext(optionA, optionB, preference)}
          disabled={!isValid}
          className="flex-1 gap-2 h-12 rounded-xl font-medium text-base"
        >
          Weiter
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
